import { Alert } from 'react-native';
import { MAX_LENGTH, STORAGE_KEY } from '../../../../shared/operator-notes';
import { z } from 'zod';
import { getApiSessionIdentity } from '../../shared/api-client';
import { getDurableItem, removeDurableItem, removePersistentItem, setDurableItem, getPersistentJson } from '../../vrs-auth/storage';
import { currentCallHistoryScope } from './callHistoryStorage';
import { mobileLog } from './logging';
export const OPERATOR_INSTRUCTIONS_KEY = STORAGE_KEY;
export const MAX_OPERATOR_INSTRUCTIONS_LENGTH = MAX_LENGTH;
export const MAX_CALL_INSTRUCTIONS_LENGTH = 1000;
const LEGACY_KEY = 'vrs_operator_instructions';
const ownerSchema = z.object({ origin: z.string().min(1), tenantId: z.string().min(1), userId: z.string().min(1), role: z.literal('client') });
const recordSchema = z.object({ version: z.literal(1), owner: ownerSchema, text: z.string().max(MAX_OPERATOR_INSTRUCTIONS_LENGTH) });
const empty = { text: '', state: 'loading', legacyUnowned: false };
let snapshot = null;
let epoch = 0, revision = 0;
let writes = Promise.resolve(true);
export function currentOperatorInstructionsScope() {
    const account = currentCallHistoryScope(), session = getApiSessionIdentity();
    const user = getPersistentJson('vrs_user_info');
    return account && session && (!user?.role || user.role === 'client') && ownerSchema.safeParse(account.owner).success ? { account, session, epoch } : null;
}
export function isCurrentOperatorInstructionsScope(scope) {
    const current = currentOperatorInstructionsScope();
    return Boolean(current && scope.epoch === current.epoch && scope.account.key === current.account.key && scope.session === current.session);
}
const ownerKey = (scope) => JSON.stringify(scope.account.owner);
export function readOperatorInstructions(scope = currentOperatorInstructionsScope()) {
    return scope && isCurrentOperatorInstructionsScope(scope) && snapshot?.owner === ownerKey(scope) ? snapshot.value : empty;
}
/** A storage timeout releases the caller, while actual writes remain ordered before cleanup. */
function bounded(operation) {
    return new Promise(resolve => {
        let active = true;
        const timer = setTimeout(() => { active = false; resolve(false); }, 15000);
        void operation(() => active).then(result => {
            if (active) {
                active = false;
                clearTimeout(timer);
                resolve(result);
            }
        }, () => { if (active) {
            active = false;
            clearTimeout(timer);
            resolve(false);
        } });
    });
}
export async function hydrateOperatorInstructions(scope = currentOperatorInstructionsScope()) {
    if (!scope || !isCurrentOperatorInstructionsScope(scope))
        return false;
    const started = revision;
    const ok = await bounded(async (active) => {
        await writes;
        if (!active() || !isCurrentOperatorInstructionsScope(scope))
            return false;
        const raw = await getDurableItem(OPERATOR_INSTRUCTIONS_KEY);
        const legacyUnowned = raw === null && Boolean(await getDurableItem(LEGACY_KEY));
        if (!active() || !isCurrentOperatorInstructionsScope(scope))
            return false;
        if (revision !== started)
            return readOperatorInstructions(scope).state === 'ready';
        if (raw === null) {
            snapshot = { owner: ownerKey(scope), value: { text: '', state: 'ready', legacyUnowned } };
            return true;
        }
        const record = recordSchema.parse(JSON.parse(raw));
        snapshot = { owner: ownerKey(scope), value: { text: JSON.stringify(record.owner) === ownerKey(scope) ? record.text : '',
                state: 'ready', legacyUnowned: false } };
        return true;
    });
    if (!ok && revision === started && isCurrentOperatorInstructionsScope(scope)) {
        snapshot = { owner: ownerKey(scope), value: { ...readOperatorInstructions(scope), state: 'unavailable' } };
        mobileLog('warn', 'operator_instructions_read_unconfirmed', {});
    }
    return ok;
}
export function saveOperatorInstructions(scope, text) {
    if (!isCurrentOperatorInstructionsScope(scope))
        return Promise.resolve(false);
    const checked = recordSchema.safeParse({ version: 1, owner: scope.account.owner, text });
    if (!checked.success)
        return Promise.resolve(false);
    const started = ++revision, raw = JSON.stringify(checked.data);
    return bounded(active => {
        writes = writes.then(async () => {
            if (!active() || !isCurrentOperatorInstructionsScope(scope))
                return false;
            await setDurableItem(OPERATOR_INSTRUCTIONS_KEY, raw);
            const receipt = await getDurableItem(OPERATOR_INSTRUCTIONS_KEY);
            if (!active() || !isCurrentOperatorInstructionsScope(scope) || revision !== started)
                return false;
            if (receipt !== raw)
                throw new Error('Unconfirmed instructions write');
            snapshot = { owner: ownerKey(scope), value: { text, state: 'ready', legacyUnowned: false } };
            return true;
        }).catch(() => { mobileLog('warn', 'operator_instructions_write_unconfirmed', {}); return false; });
        return writes;
    });
}
export function clearOperatorInstructions() {
    epoch++;
    revision++;
    snapshot = null;
    removePersistentItem(LEGACY_KEY);
    return bounded(() => {
        writes = writes.then(async () => {
            await removeDurableItem(OPERATOR_INSTRUCTIONS_KEY);
            await removeDurableItem(LEGACY_KEY);
            return true;
        }).catch(() => { mobileLog('warn', 'operator_instructions_cleanup_unconfirmed', {}); return false; });
        return writes;
    });
}
export function beginOperatorInstructionsCleanup() {
    void clearOperatorInstructions().then(ok => {
        if (ok)
            return;
        mobileLog('warn', 'operator_instructions_cleanup_unconfirmed', {});
        try {
            Alert.alert('Private instructions cleanup incomplete', 'Earlier call instructions may remain on this device.');
        }
        catch {
            mobileLog('warn', 'operator_instructions_cleanup_notice_failed', {});
        }
    });
}
/** Preserve the complete private note and existing route context; never silently truncate a call request. */
export function checkCallInstructions(saved, composed) {
    if (saved.state !== 'ready')
        return { value: '', error: 'Saved call instructions unavailable. Open Settings.' };
    if (composed.length > MAX_CALL_INSTRUCTIONS_LENGTH)
        return { value: '', error: 'Saved instructions too long. Shorten them in Settings.' };
    return { value: composed };
}
